import { httpRequest } from "@services/http"

/**
 * Commit a campaign's whole budget draft (MPN-1802).
 *
 * PUT, not POST: the payload is the campaign's entire budget, so a window left out of
 * `segments` is one the operator deleted. The server commits or rejects it as a unit, which
 * is what the confirmation dialogs on the screen already promise — a partial apply would
 * leave a campaign in a state the screen would have refused to let anyone build.
 *
 * `margin` is omitted when a save only moves windows. Restating it would make an unrelated
 * edit read as a margin change in History.
 */
export type LaunchBudgetAdGroupPayload = {
  ad_group_id: number
  percent: number
  daily_spend_cap?: number | null
  bid?: number | null
  /** Paused on this network for this window (MPN-1893). A resumed ad group is omitted from
   *  the list instead, which removes its override and puts it back on the campaign's split. */
  is_paused?: boolean
}

/**
 * One network's share of one window. `daily_spend_cap` and `bid` travel with the percent
 * because they are part of the same BUDGET policy spec, and a window-scoped policy beats
 * the campaign's outright — sending the percent alone would drop the cap for that window.
 */
export type LaunchBudgetSegmentNetworkPayload = {
  network: string
  percent: number
  daily_spend_cap?: number | null
  bid?: number | null
  /** Paused for this window (MPN-1893). Carried beside the percent rather than inferred from
   *  it: a resume brings the network back at 0% and NOT paused, so Ops reallocates on
   *  purpose instead of having a stale split restored against a balance that has moved. */
  is_paused?: boolean
  ad_groups?: LaunchBudgetAdGroupPayload[]
}

export type LaunchBudgetSegmentPayload = {
  /** Absent for a window the operator has just drawn. */
  id?: number
  name?: string
  start_time: string
  end_time: string
  amount: number
  /**
   * This window's own split: the networks set by hand on it, and only those. Omit the key
   * entirely to leave the window's existing overrides alone; send `[]` to clear them. The
   * two are different, and the distinction is what keeps a Save that only moved a date
   * from wiping a split someone set.
   */
  networks?: LaunchBudgetSegmentNetworkPayload[]
}

export type LaunchBudgetSavePayload = {
  spend_pattern: "LEGACY" | "EVEN" | "MONTHLY" | "CUSTOM"
  segments: LaunchBudgetSegmentPayload[]
  margin?: number
  /**
   * The campaign-wide split, for anything that is not a per-window override — Daily Cap in
   * particular, which belongs to the network across the whole campaign (MPN-1806). Omit
   * the key to leave the campaign's existing policies alone; the server rebuilds a
   * policy's whole spec from what it is sent, so only send a network you mean to rewrite.
   */
  networks?: LaunchBudgetSegmentNetworkPayload[]
}

export class CommandSaveLaunchBudget {
  async run(launchId: number, data: LaunchBudgetSavePayload): Promise<void> {
    const url = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/engagements/launches/${launchId}/budget/`
    await httpRequest(url, { method: "PUT", body: data })
  }
}
